import { cmdkFilter } from "@/lib/cmdk-filter"

const IDENTIFIER = /^[a-z][a-z0-9_]*-\d+$/i
const ISSUE_LINK = /linear\.app\/[^/\s]+\/issue\/([a-z][a-z0-9_]*-\d+)/i
const PROJECT_LINK = /linear\.app\/[^/\s]+\/project\/([^/?#\s]+)/i

export interface IssueSearchTerm {
	/** What the issue list matches on: the typed text, or the identifier a pasted link points to. */
	text: string
	/** Set when the term is exactly one issue identifier, typed or pasted as a link. */
	identifier: string | null
	/** Set when a Linear project link was pasted; only that project should match. */
	projectSlugId: string | null
}

// Linear project URLs end in "<name-slug>-<slugId>", and the name part changes on rename.
function projectSlugId(pathSegment: string): string | null {
	const slugId = pathSegment.split("-").pop()
	return slugId ? slugId.toLowerCase() : null
}

export function parseIssueSearch(query: string): IssueSearchTerm {
	const trimmed = query.trim()

	const issueLink = ISSUE_LINK.exec(trimmed)
	if (issueLink?.[1]) {
		const identifier = issueLink[1].toUpperCase()
		return { text: identifier, identifier, projectSlugId: null }
	}

	const projectLink = PROJECT_LINK.exec(trimmed)
	if (projectLink?.[1]) {
		return {
			text: "",
			identifier: null,
			projectSlugId: projectSlugId(projectLink[1]),
		}
	}

	return {
		text: trimmed,
		identifier: IDENTIFIER.test(trimmed) ? trimmed.toUpperCase() : null,
		projectSlugId: null,
	}
}

export function matchesProjectLink(
	project: { url?: string | null },
	slugId: string,
): boolean {
	if (!project.url) return false
	const segment = PROJECT_LINK.exec(project.url)?.[1]
	return !!segment && projectSlugId(segment) === slugId
}

interface SearchableIssue {
	identifier: string
	title: string
	status?: string | null
	previousIdentifiers?: ReadonlyArray<string> | null
}

export interface IndexedIssue<T> {
	issue: T
	haystack: string
	title: string
	identifiers: Array<string>
}

/** Lowercases every issue once, so a keystroke only runs indexOf over the list. */
export function indexIssues<T extends SearchableIssue>(
	issues: ReadonlyArray<T>,
): Array<IndexedIssue<T>> {
	return issues.map((issue) => {
		// An issue that moved teams is still found by its old identifier.
		const identifiers = [
			issue.identifier,
			...(issue.previousIdentifiers ?? []),
		].map((identifier) => identifier.toUpperCase())
		return {
			issue,
			identifiers,
			title: issue.title.toLowerCase(),
			haystack: `${identifiers.join(" ")} ${issue.title}`.toLowerCase(),
		}
	})
}

const STATUS_RANK: Record<string, number> = { completed: 1, canceled: 2 }

function statusRank(status: string | null | undefined): number {
	return status ? (STATUS_RANK[status] ?? 0) : 0
}

/**
 * Exact identifier hits first, then open, completed and canceled issues, each
 * group by match quality. Equal matches keep their incoming order.
 */
export function rankIssues<T extends SearchableIssue>(
	index: ReadonlyArray<IndexedIssue<T>>,
	term: IssueSearchTerm,
): Array<T> {
	if (term.projectSlugId) return []

	const scored: Array<{
		issue: T
		exact: boolean
		score: number
		titleScore: number
		order: number
	}> = []
	index.forEach((entry, order) => {
		const exact =
			!!term.identifier && entry.identifiers.includes(term.identifier)
		const score = term.text ? cmdkFilter(entry.haystack, term.text) : 1
		if (!exact && score === 0) return
		// The identifier prefix pushes every title match off the haystack start.
		const titleScore = term.text ? cmdkFilter(entry.title, term.text) : 0
		scored.push({ issue: entry.issue, exact, score, titleScore, order })
	})

	return scored
		.sort(
			(a, b) =>
				Number(b.exact) - Number(a.exact) ||
				statusRank(a.issue.status) - statusRank(b.issue.status) ||
				b.score - a.score ||
				b.titleScore - a.titleScore ||
				a.order - b.order,
		)
		.map((entry) => entry.issue)
}

/**
 * Linear's full-text search also matches descriptions and comments, which the
 * picker never shows, so a hit counts only if its title or identifier matches
 * the way a synced issue would. Linear's order is kept so later pages append.
 */
export function titleMatchedIssues<T extends SearchableIssue>(
	issues: ReadonlyArray<T>,
	term: IssueSearchTerm,
): Array<T> {
	if (!term.text) return [...issues]
	return indexIssues(issues)
		.filter((entry) => cmdkFilter(entry.haystack, term.text) > 0)
		.map((entry) => entry.issue)
}

/**
 * Issues Linear's search found that the sync doesn't have. They go after the
 * local rows, so nothing the user is looking at moves when they arrive.
 */
export function remoteOnlyIssues<T extends { linearTicketId: string }>(
	local: ReadonlyArray<{ linearTicketId: string }>,
	remote: ReadonlyArray<T>,
): Array<T> {
	if (remote.length === 0) return []
	const seen = new Set(local.map((issue) => issue.linearTicketId))
	return remote.filter((issue) => {
		if (seen.has(issue.linearTicketId)) return false
		seen.add(issue.linearTicketId)
		return true
	})
}

export const ISSUE_PAGE_SIZE = 40

/** Linear is only asked when the synced issues run short or the user scrolls past them. */
export function wantsRemoteSearch({
	remoteText,
	localText,
	localCount,
	reachedEnd,
}: {
	remoteText: string
	localText: string
	localCount: number
	reachedEnd: boolean
}): boolean {
	// The debounced text trails the typed one; asking for a stale term is wasted.
	if (!remoteText || remoteText !== localText) return false
	return localCount < ISSUE_PAGE_SIZE || reachedEnd
}

export function nextIssuePage({
	limit,
	total,
	remoteReady,
	remoteActive,
	hasNextPage,
	isFetchingNextPage,
}: {
	limit: number
	total: number
	/** The remote term matches what the list shows. */
	remoteReady: boolean
	remoteActive: boolean
	hasNextPage: boolean
	isFetchingNextPage: boolean
}): { limit: number; reachEnd: boolean; fetchNext: boolean } {
	if (limit < total) {
		return {
			limit: limit + ISSUE_PAGE_SIZE,
			reachEnd: false,
			fetchNext: false,
		}
	}
	if (!remoteReady) return { limit, reachEnd: false, fetchNext: false }
	// Room for the page Linear is about to send.
	const next = total + ISSUE_PAGE_SIZE
	if (!remoteActive) return { limit: next, reachEnd: true, fetchNext: false }
	if (!hasNextPage || isFetchingNextPage) {
		return { limit, reachEnd: false, fetchNext: false }
	}
	return { limit: next, reachEnd: false, fetchNext: true }
}
